import { formatLargeCurrency, getLanguage, getTextWidth, roundToNiceNumber } from "./utilities";

import Formatter from "@rmlibrary/formatting";
import { Point } from "@rmlibrary/graph";

const white = "#ffffff";
const lang = getLanguage();
const formatter = new Formatter(lang);

function getLineGraphCSS({ badStart, goodStart, screenPixelRatio, screenWidth, stableReturn }) {
    function getRightPadding() {
        let finalPortfolioValue = 0;
        let maxFinalPortfolioValue = 0;

        if (badStart) {
            finalPortfolioValue = badStart.slice(-1)[0].portfolioValue;
            maxFinalPortfolioValue = Math.max(finalPortfolioValue, maxFinalPortfolioValue);
        }

        if (goodStart) {
            finalPortfolioValue = goodStart.slice(-1)[0].portfolioValue;
            maxFinalPortfolioValue = Math.max(finalPortfolioValue, maxFinalPortfolioValue);
        }

        if (stableReturn) {
            finalPortfolioValue = stableReturn.slice(-1)[0].portfolioValue;
            maxFinalPortfolioValue = Math.max(finalPortfolioValue, maxFinalPortfolioValue);
        }

        maxFinalPortfolioValue = formatter.formatCurrency(maxFinalPortfolioValue, 0);
        const offset = screenWidth === "small" ? 30 : 100;
        const textWidth = getTextWidth(`${maxFinalPortfolioValue}`, "bold 15px Roobert-bold");

        return offset + textWidth;
    }

    const title = {
        font: "Roobert-bold",
        weight: "bold",
        size: 16 * screenPixelRatio,
        colour: "#161616",
        rotateYLabel: true,
        xLabelTextAlign: "center",
        xLabelTextVerticalAlign: "right",
        yLabelTextAlign: "center",
        yLabelTextHorizontalAlign: "center",
    };
    const numbers = {
        xFormat: (number) => number.toFixed(0),
        yFormat: (number) => {
            if (screenWidth === "small") {
                number = formatLargeCurrency(number);
            } else {
                number = formatter.formatCurrency(number, 0);
            }

            return number;
        },
        font: "Roobert",
        size: 16 * screenPixelRatio,
        padding: {
            xAxis: 10 * screenPixelRatio,
            yAxis: 10 * screenPixelRatio,
        },
        xAxisWeight: "bold",
        yAxisWeight: "bold",
        colour: "#161616",
    };
    const axisCss = {
        axisLineWidth: 1 * screenPixelRatio,
        axisLineColour: "#161616",
        gridLineWidth: 1 * screenPixelRatio,
        gridLineColour: "#161616",
        title,
        numbers,
    };

    const left = screenWidth === "small" ? 65 : 100;
    const padding = {
        left: left * screenPixelRatio,
        right: getRightPadding() * screenPixelRatio,
        top: 15 * screenPixelRatio,
        bottom: 60 * screenPixelRatio,
    };
    const canvasCss = { padding };

    const titleCss = {
        font: "Roobert-bold",
        weight: "bold",
        size: 20 * screenPixelRatio,
        colour: "#161616",
        textAlign: "center",
    };

    return {
        axisCss,
        canvasCss,
        titleCss,
    };
}

function getLineGraphDataset({
    badStart,
    drawCallback,
    goodStart,
    lineColourOne,
    lineColourTwo,
    lineColourThree,
    screenPixelRatio,
    screenWidth,
    stableReturn,
}) {
    function createPoint(name) {
        return (data) => {
            const { year, portfolioValue } = data;

            return new Point(year, portfolioValue, drawCallback({ ...data, name, screenPixelRatio, screenWidth }));
        };
    }

    function includePoint(element, index) {
        return screenWidth === "small" ? index % 2 === 0 : true;
    }

    const pointsCSS = {
        colour: white,
        size: 8 * screenPixelRatio,
        style: "circle",
    };
    let dataset = [];
    let name = "";

    if (badStart) {
        name = "Bad Start";
        dataset = dataset.concat({
            css: {
                width: 5 * screenPixelRatio,
                colour: lineColourOne,
                points: pointsCSS,
            },
            name,
            points: badStart.map(createPoint(name)).filter(includePoint),
            type: "line",
        });
    }

    if (goodStart) {
        name = "Good Start";
        dataset = dataset.concat({
            css: {
                width: 5 * screenPixelRatio,
                colour: lineColourTwo,
                points: pointsCSS,
            },
            name,
            points: goodStart.map(createPoint(name)).filter(includePoint),
            type: "line",
        });
    }

    if (stableReturn) {
        name = "Stable Return";
        dataset = dataset.concat({
            css: {
                width: 5 * screenPixelRatio,
                colour: lineColourThree,
                points: pointsCSS,
            },
            name,
            points: stableReturn.map(createPoint(name)).filter(includePoint),
            type: "line",
        });
    }

    return dataset;
}

function getLineGraphAxesLimits({ badStart, goodStart, stableReturn }) {
    const xAxisMin = badStart.reduce((min, { year }) => Math.min(min, year), Number.POSITIVE_INFINITY);
    const xAxisMax = badStart.reduce((max, { year }) => Math.max(max, year), Number.NEGATIVE_INFINITY);

    const badStartPortfolioValues = badStart.map(({ portfolioValue }) => portfolioValue);
    const goodStartPortfolioValues = goodStart.map(({ portfolioValue }) => portfolioValue);
    const stableReturnPortfolioValues = stableReturn.map(({ portfolioValue }) => portfolioValue);
    const portfolioValues = [...badStartPortfolioValues, ...goodStartPortfolioValues, ...stableReturnPortfolioValues];
    const yAxisMin = Math.min(...portfolioValues, 0);
    const yAxisMax = roundToNiceNumber(Math.max(...portfolioValues), 6);

    return {
        xAxis: {
            max: xAxisMax,
            min: xAxisMin,
        },
        yAxis: {
            max: yAxisMax,
            min: yAxisMin,
        },
    };
}

function getLineGraphOptions({
    badStart,
    goodStart,
    hidePopup,
    screenPixelRatio,
    screenWidth,
    showPopup,
    stableReturn,
}) {
    const { xAxis, yAxis } = getLineGraphAxesLimits({ badStart, goodStart, stableReturn });
    let xAxisTicks = xAxis.max - xAxis.min + 1;
    if (screenWidth === "small") {
        xAxisTicks = Math.ceil(xAxisTicks / 2);
    }

    const yAxisTicks = 7;

    const axisOptions = {
        xAxis: {
            name: lang === "en" ? "Years" : "Ans",
            drawAxisLine: true,
            drawGridLines: false,
            range: [xAxis.min, xAxis.max],
            removeTicks: [0],
            ticks: xAxisTicks,
        },
        yAxis: {
            name: "",
            drawAxisLine: true,
            drawGridLines: true,
            range: [yAxis.min, yAxis.max],
            removeTicks: [0],
            ticks: yAxisTicks,
            gridStyle: "dashed",
        },
    };
    const events = {
        hoverOn: showPopup,
        hoverOff: hidePopup,
        clickOn: showPopup,
        clickOff: hidePopup,
    };

    return {
        accessibilityTable: false,
        autoResize: false,
        axisOptions,
        events,
        pixelRatio: screenPixelRatio,
        title: "",
    };
}

export { getLineGraphAxesLimits, getLineGraphCSS, getLineGraphDataset, getLineGraphOptions };
